import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../config';
import { useCurrency } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';
import './EstimateurValeur.css';

const TYPES = ['Appartement', 'Villa', 'Duplex', 'Studio', 'Terrain'];

function EstimateurValeur() {
  const { t } = useI18n();
  const [quartiers, setQuartiers] = useState([]);
  const [formData, setFormData] = useState({
    quartier: '', type: '', surface: '', chambres: ''
  });
  const [resultat, setResultat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/tools/quartiers`)
      .then(res => res.json())
      .then(data => setQuartiers(data.quartiers || []))
      .catch(() => {});
  }, []);

  const { formatPrix } = useCurrency();

  const estimer = async () => {
    if (!formData.quartier) return;
    setLoading(true);
    setError(null);
    setResultat(null);

    try {
      const params = new URLSearchParams();
      params.append('quartier', formData.quartier);
      if (formData.type) params.append('type', formData.type);
      if (formData.surface) params.append('surface', formData.surface);
      if (formData.chambres) params.append('chambres', formData.chambres);

      const res = await fetch(`${API_URL}/tools/estimateur?${params}`);
      const data = await res.json();
      setResultat(data);
    } catch {
      setError(t.tools.estimError);
    } finally {
      setLoading(false);
    }
  };

  const confidenceColor = (c) => {
    if (c === 'haute') return 'var(--success)';
    if (c === 'moyenne') return 'var(--warning)';
    return 'var(--danger)';
  };

  const confidenceLabel = (c) => {
    if (c === 'haute') return t.tools.confHigh;
    if (c === 'moyenne') return t.tools.confMid;
    if (c === 'faible') return t.tools.confLow;
    return t.tools.confNone;
  };

  return (
    <div className="estimateur-page">
      <Link to="/outils" className="btn btn-secondary">{t.tools.backToTools}</Link>

      <h1>{t.tools.estimTitle}</h1>
      <p className="est-subtitle">{t.tools.estimDesc}</p>

      <div className="est-layout">
        <div className="est-form glass-card">
          <div className="form-group">
            <label>{t.tools.quartierLabel} *</label>
            <select
              value={formData.quartier}
              onChange={e => setFormData({...formData, quartier: e.target.value})}
            >
              <option value="">{t.tools.selectQuartier}</option>
              {quartiers.map(q => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t.tools.typeLogement}</label>
            <select
              value={formData.type}
              onChange={e => setFormData({...formData, type: e.target.value})}
            >
              <option value="">{t.tools.allTypesOption}</option>
              {TYPES.map(tp => (
                <option key={tp} value={tp}>{tp}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t.tools.surfaceM2}</label>
              <input
                type="number"
                placeholder="Ex: 120"
                value={formData.surface}
                onChange={e => setFormData({...formData, surface: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>{t.tools.roomsLabel}</label>
              <input
                type="number"
                placeholder="Ex: 3"
                value={formData.chambres}
                onChange={e => setFormData({...formData, chambres: e.target.value})}
              />
            </div>
          </div>

          <button
            className="btn btn-primary calc-btn"
            onClick={estimer}
            disabled={!formData.quartier || loading}
          >
            {loading ? t.tools.estimLoading : t.tools.estimBtn}
          </button>

          <p className="disclaimer">{t.tools.toolsDisclaimer}</p>
        </div>

        {error && <p className="error">{error}</p>}

        {resultat && (
          <div className="est-results glass-card">
            <div className="est-results-header">
              <h2>{t.tools.estimFor} {formData.quartier}</h2>
              <span
                className="confidence-badge"
                style={{ background: confidenceColor(resultat.confidence) }}
              >
                {confidenceLabel(resultat.confidence)}
              </span>
            </div>

            {resultat.comparables === 0 ? (
              <p className="no-data">{resultat.message}</p>
            ) : (
              <>
                {resultat.estimation && (
                  <div className="estimation-range">
                    <span className="est-label">{t.tools.estimatedValue}</span>
                    <div className="range-bar">
                      <div className="range-bar-fill"></div>
                      <div className="range-labels-bar">
                        <span>{formatPrix(resultat.estimation.basse)}</span>
                        <strong>{formatPrix(resultat.estimation.moyenne)}</strong>
                        <span>{formatPrix(resultat.estimation.haute)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="stats-grid">
                  <div className="stat-card">
                    <span className="stat-value">{formatPrix(resultat.prixMoyen)}</span>
                    <span className="stat-label">{t.tools.avgPrice}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{formatPrix(resultat.prixM2Moyen)}</span>
                    <span className="stat-label">{t.tools.avgPriceM2}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{resultat.surfaceMoyenne} m²</span>
                    <span className="stat-label">{t.tools.avgSurface}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{resultat.comparables}</span>
                    <span className="stat-label">{t.tools.comparables}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{formatPrix(resultat.prixMin)}</span>
                    <span className="stat-label">{t.tools.minPrice}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{formatPrix(resultat.prixMax)}</span>
                    <span className="stat-label">{t.tools.maxPrice}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EstimateurValeur;
